import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { updateCompany } from '@/utils/companiesHelper';
import { useAppSelector } from '@/hooks/useAppSelector';

interface EditCompanyProps {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  currentEmployees: string;
  currentCategory: string;
  currentScraper?: string;
}

export function EditCompany({
  isOpen,
  onClose,
  slug,
  currentEmployees,
  currentCategory,
  currentScraper = ""
}: EditCompanyProps) {
  const [employees, setEmployees] = useState(currentEmployees);
  const [category, setCategory] = useState(currentCategory);
  // Map empty string to 'default' for UI display
  const [scraper, setScraper] = useState(currentScraper === '' || !currentScraper ? 'default' : currentScraper);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  
  const uniqueCategories = useAppSelector(state => state.supaCompanies.uniqueCategories)
    .filter(cat => cat !== 'All');
  const uniqueEmployees = useAppSelector(state => state.supaCompanies.uniqueEmployees)
    .filter(emp => emp !== 'All');

  const handleSave = async () => {
    try {
      setIsLoading(true);
      await updateCompany({
        slug,
        employees,
        category,
        scraper
      });
      
      toast({
        title: "Success",
        description: "Company details updated successfully",
      });
      onClose();
    } catch (error) {
      console.error('❌ [EditCompany] Error updating company:', error);
      toast({
        title: "Error",
        description: "Failed to update company details",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Company Details</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="employees">Employees</Label>
            <Select
              value={employees}
              onValueChange={setEmployees}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select company size" />
              </SelectTrigger>
              <SelectContent>
                {uniqueEmployees.map((size) => (
                  <SelectItem key={size} value={size}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select
              value={category}
              onValueChange={setCategory}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {uniqueCategories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="scraper">Screenshot Method</Label>
            <Select
              value={scraper}
              onValueChange={setScraper}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select screenshot method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default</SelectItem>
                <SelectItem value="scrapingBee">ScrapingBee</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? "Saving..." : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}